import axios from "axios";
import type {
  AuthToken,
  Deployment,
  DeploymentKind,
  Environment,
  HyperParams,
  LoginRequest,
  Project,
  RegisterRequest,
  RobotConnection,
  RobotPlacement,
  SafetyEnvelopeConfig,
  TrainingJob,
  UploadProgress,
  User,
  VlaStats,
  ContactRequest,
  ContactReceipt,
} from "./types";

// VITE_API_URL is the platform-api origin set at build time
// (e.g. https://api.example.com).
// In production builds this MUST be set; otherwise we fall back to a relative
// /api/v1 which only works in local dev (where Vite proxies to the backend).
const API_ORIGIN = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");
const BASE_URL = API_ORIGIN ? `${API_ORIGIN}/api/v1` : "/api/v1";

// Loud warning in the browser console when a production bundle is missing
// VITE_API_URL — without this, every request hits the frontend hosting domain
// instead of the platform-api and can return confusing SPA fallback errors.
if (import.meta.env.PROD && !API_ORIGIN) {
  // eslint-disable-next-line no-console
  console.error(
    "[RoboTwins] VITE_API_URL is not set. API requests will fail because " +
    "they will be sent to the frontend hosting domain instead of the platform-api. " +
    "Set VITE_API_URL in the hosting environment variables and redeploy.",
  );
}

// ── Token storage ─────────────────────────────────────────────────────
const TOKEN_KEY = "robotwins.auth.token";

export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setStoredToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* ignore (private mode etc.) */
  }
}

export function clearStoredToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

// Optional callback the store registers so we can reset auth state on 401.
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

const client = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
  timeout: 30_000,
});

// Attach Authorization: Bearer <token> from localStorage on every request.
client.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

client.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response?.status === 401) {
      // Token is missing/invalid/expired — drop it so AuthGate routes to /login.
      clearStoredToken();
      if (onUnauthorized) onUnauthorized();
    }
    const message =
      error.response?.data?.detail ??
      error.response?.data?.message ??
      error.message ??
      "An unexpected error occurred";
    return Promise.reject(new Error(message));
  },
);

/* ------------------------------------------------------------------ */
/*  Auth                                                              */
/* ------------------------------------------------------------------ */

export async function login(payload: LoginRequest): Promise<AuthToken> {
  const { data } = await client.post<AuthToken>("/auth/login", payload);
  return data;
}

export async function register(payload: RegisterRequest): Promise<User> {
  const { data } = await client.post<User>("/auth/register", payload);
  return data;
}

export async function getMe(): Promise<User> {
  const { data } = await client.get<User>("/auth/me");
  return data;
}

/* ------------------------------------------------------------------ */
/*  Projects                                                          */
/* ------------------------------------------------------------------ */

export async function listProjects(): Promise<Project[]> {
  const { data } = await client.get<Project[]>("/projects");
  return data;
}

export async function getProject(id: string): Promise<Project> {
  const { data } = await client.get<Project>(`/projects/${id}`);
  return data;
}

export async function createProject(
  payload: Pick<Project, "name" | "description">,
): Promise<Project> {
  const { data } = await client.post<Project>("/projects", payload);
  return data;
}

export async function deleteProject(id: string): Promise<void> {
  await client.delete(`/projects/${id}`);
}

/* ------------------------------------------------------------------ */
/*  Environments                                                       */
/* ------------------------------------------------------------------ */

export async function listEnvironments(projectId: string): Promise<Environment[]> {
  const { data } = await client.get<Environment[]>(
    `/projects/${projectId}/environments`,
  );
  return data;
}

// platform-api serves single environments on a flat route, not nested
// under the project (routes/environments.py).
export async function getEnvironment(
  _projectId: string,
  envId: string,
): Promise<Environment> {
  const { data } = await client.get<Environment>(`/environments/${envId}`);
  return data;
}

export async function uploadScan(
  projectId: string,
  files: File[],
  name: string,
  onProgress?: (progress: UploadProgress) => void,
): Promise<Environment> {
  const formData = new FormData();
  formData.append("name", name);
  for (const file of files) {
    formData.append("files", file);
  }

  const { data } = await client.post<Environment>(
    `/projects/${projectId}/environments`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 600_000,
      onUploadProgress(event) {
        if (onProgress && event.total) {
          onProgress({
            file_name: files[0]?.name ?? "upload",
            loaded: event.loaded,
            total: event.total,
            percentage: Math.round((event.loaded / event.total) * 100),
          });
        }
      },
    },
  );
  return data;
}

export async function deleteEnvironment(
  _projectId: string,
  envId: string,
): Promise<void> {
  await client.delete(`/environments/${envId}`);
}

/* ------------------------------------------------------------------ */
/*  Robot Placement (within an environment)                            */
/* ------------------------------------------------------------------ */

export async function placeRobot(
  projectId: string,
  envId: string,
  placement: RobotPlacement,
): Promise<void> {
  await client.post(
    `/projects/${projectId}/environments/${envId}/robot`,
    placement,
  );
}

/* ------------------------------------------------------------------ */
/*  Training Jobs                                                      */
/* ------------------------------------------------------------------ */

export async function listTrainingJobs(
  projectId: string,
): Promise<TrainingJob[]> {
  const { data } = await client.get<TrainingJob[]>(
    `/projects/${projectId}/training-jobs`,
  );
  return data;
}

// Single jobs live on a flat route (routes/training.py).
export async function getTrainingJob(
  _projectId: string,
  jobId: string,
): Promise<TrainingJob> {
  const { data } = await client.get<TrainingJob>(`/training-jobs/${jobId}`);
  return data;
}

export async function startTraining(
  projectId: string,
  payload: {
    name: string;
    environment_id: string;
    model_type: string;
    scenario: string;
    hyperparams: HyperParams;
  },
): Promise<TrainingJob> {
  const { data } = await client.post<TrainingJob>(
    `/projects/${projectId}/training-jobs`,
    payload,
  );
  return data;
}

export async function stopTraining(
  _projectId: string,
  jobId: string,
): Promise<TrainingJob> {
  const { data } = await client.post<TrainingJob>(
    `/training-jobs/${jobId}/stop`,
  );
  return data;
}

/* ------------------------------------------------------------------ */
/*  Deployments                                                        */
/* ------------------------------------------------------------------ */

// platform-api has no deployment *list* route yet (routes/deployments.py
// exposes create + flat per-id routes only). Treat the missing endpoint as
// an empty list instead of surfacing a global error for every project view.
export async function listDeployments(
  projectId: string,
): Promise<Deployment[]> {
  try {
    const { data } = await client.get<Deployment[]>(
      `/projects/${projectId}/deployments`,
    );
    return data;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(
      "[console] deployment list endpoint not available yet:",
      (e as Error).message,
    );
    return [];
  }
}

export async function getDeployment(
  _projectId: string,
  deploymentId: string,
): Promise<Deployment> {
  const { data } = await client.get<Deployment>(
    `/deployments/${deploymentId}`,
  );
  return data;
}

export interface CreateDeploymentRequest {
  robot_connection_id: string;
  safety_config: SafetyEnvelopeConfig;
  kind?: DeploymentKind;
  /** Required when kind === "trained_policy" (or kind is omitted). */
  training_job_id?: string;
  /** Required when kind === "vla_generalist". */
  prompt?: string;
  /**
   * Silent trial: run the whole stack and never command the robot. The only
   * deployment shape with no mechanism of harm.
   */
  observe_only?: boolean;
  control_hz?: number;
  max_steps?: number;
  /** Override the VLA endpoint for this deployment, e.g. a bench stub. */
  cognition_service_url?: string;
}

export async function createDeployment(
  projectId: string,
  payload: CreateDeploymentRequest,
): Promise<Deployment> {
  const { data } = await client.post<Deployment>(
    `/projects/${projectId}/deployments`,
    payload,
  );
  return data;
}

export async function startDeployment(
  _projectId: string,
  deploymentId: string,
): Promise<Deployment> {
  const { data } = await client.post<Deployment>(
    `/deployments/${deploymentId}/start`,
  );
  return data;
}

export async function stopDeployment(
  _projectId: string,
  deploymentId: string,
): Promise<Deployment> {
  const { data } = await client.post<Deployment>(
    `/deployments/${deploymentId}/stop`,
  );
  return data;
}

export async function emergencyStop(
  _projectId: string,
  deploymentId: string,
): Promise<Deployment> {
  const { data } = await client.post<Deployment>(
    `/deployments/${deploymentId}/emergency-stop`,
  );
  return data;
}

export async function getVlaStats(deploymentId: string): Promise<VlaStats> {
  const { data } = await client.get<VlaStats>(
    `/deployments/${deploymentId}/vla-stats`,
  );
  return data;
}

/* ------------------------------------------------------------------ */
/*  Robot Connections                                                   */
/* ------------------------------------------------------------------ */

export async function listRobotConnections(): Promise<RobotConnection[]> {
  const { data } = await client.get<RobotConnection[]>("/robots");
  return data;
}

export interface CreateRobotConnectionRequest {
  manufacturer: RobotConnection["manufacturer"];
  host: string;
  port?: number;
  model?: string;
  name?: string;
}

export async function createRobotConnection(
  payload: CreateRobotConnectionRequest,
): Promise<RobotConnection> {
  const { data } = await client.post<RobotConnection>("/robots", payload);
  return data;
}

export async function deleteRobotConnection(robotId: string): Promise<void> {
  await client.delete(`/robots/${robotId}`);
}

export async function getRobotConnection(
  robotId: string,
): Promise<RobotConnection> {
  const { data } = await client.get<RobotConnection>(`/robots/${robotId}`);
  return data;
}

/* ------------------------------------------------------------------ */
/*  Cognition                                                          */
/* ------------------------------------------------------------------ */

export interface GroundedObject {
  id: string;
  label: string;
  color?: string;
  bbox_2d?: [number, number, number, number];
  approximate_3d_position?: [number, number, number];
  affordances?: string[];
}

export interface TaskSpec {
  task_type: string;
  primary_object?: string | null;
  secondary_object?: string | null;
  target_pose?: number[] | null;
  success_criteria: any[];
  constraints: any[];
  estimated_episode_length_steps: number;
  natural_language_request?: string | null;
}

export interface RewardSynthesis {
  accepted: boolean;
  source: string | null;
  attempts: number;
  validation: { ok: boolean; errors: string[] };
  dry_run: { ok: boolean; n_steps?: number; rewards?: number[]; mean?: number; error?: string } | null;
  history: any[];
  review_required: boolean;
}

export interface FromLanguageResponse {
  grounded_objects: GroundedObject[];
  task_spec: TaskSpec;
  reward: RewardSynthesis;
}

export async function authorTaskFromLanguage(
  nl_request: string,
  image_b64?: string,
): Promise<FromLanguageResponse> {
  const { data } = await client.post<FromLanguageResponse>(
    "/cognition/from-language",
    { nl_request, image_b64 },
  );
  return data;
}

/* ------------------------------------------------------------------ */
/*  Restriction rules                                                   */
/* ------------------------------------------------------------------ */

export interface RuleTemplate {
  key: string;
  label: string;
  /** "A_enforced_by_robot" or "B_monitored_by_us". */
  channel: string;
  description: string;
  /** What actually happens when the rule is crossed, in plain words. */
  enforcement: string;
  parameters: string[];
  counterexample_available: boolean;
}

export interface Counterexample {
  found: boolean;
  template_key: string;
  robot_model: string;
  frames: number[][];
  tcp_path: number[][];
  violates_at: number | null;
  explanation: string;
}

export async function listRuleTemplates(): Promise<{
  templates: RuleTemplate[];
  channels: Record<string, string>;
}> {
  const { data } = await client.get<{
    templates: RuleTemplate[];
    channels: Record<string, string>;
  }>("/cognition/rules/templates");
  return data;
}

export async function ruleCounterexample(
  template_key: string,
  parameters: Record<string, number>,
  robot_model = "ur5",
): Promise<{ found: boolean; channel: string; counterexample: Counterexample }> {
  const { data } = await client.post<{
    found: boolean;
    channel: string;
    counterexample: Counterexample;
  }>("/cognition/rules/counterexample", {
    template_key,
    parameters,
    robot_model,
  });
  return data;
}

/**
 * Sign a named person's approval of a restriction.
 *
 * The API refuses an approval that was given without a counterexample being
 * shown — an approval without one is the rubber stamp this flow replaces.
 */
export async function authoriseRule(
  projectId: string,
  body: {
    template_key: string;
    channel: string;
    parameters: Record<string, number>;
    enforcement: string;
    counterexample: Counterexample;
    counterexample_shown: boolean;
  },
): Promise<LedgerEvent> {
  const { data } = await client.post<LedgerEvent>(
    `/cognition/projects/${projectId}/rules/authorise`,
    body,
  );
  return data;
}

export async function regenerateReward(task_spec: TaskSpec): Promise<RewardSynthesis> {
  const { data } = await client.post<RewardSynthesis>(
    "/cognition/rewards/synthesize",
    { task_spec, max_attempts: 3 },
  );
  return data;
}

/* ------------------------------------------------------------------ */
/*  Ledger (signed intent/egress chain)                                */
/* ------------------------------------------------------------------ */

export interface LedgerEvent {
  id: string;
  project_id: string;
  seq: number;
  channel: "intent" | "egress";
  event_type: string;
  actor: string;
  action: string;
  rationale?: string | null;
  destination?: string | null;
  payload_bytes?: number | null;
  created_at?: string | null;
  /** sha256 of the exact bytes that left, for egress entries. Signed from v2. */
  payload_digest?: string | null;
  /** Which signed-field tuple this entry was hashed over. Absent means v1. */
  sig_version?: number | null;
  prev_hash: string;
  entry_hash: string;
  signature: string;
}

export interface PublicKeyInfo {
  algorithm: string;
  public_key: string;
  key_ephemeral: boolean;
}

/**
 * The verification key, deliberately unauthenticated on the server.
 *
 * A verifier that only the issuer can fetch verifies nothing. This is what
 * lets a browser check a signature without trusting the API that served it.
 */
export async function getLedgerPublicKey(): Promise<PublicKeyInfo> {
  const { data } = await client.get<PublicKeyInfo>("/ledger/public-key");
  return data;
}

export interface ReplayResult {
  steps_checked: number;
  divergence: number;
  diverged_at: number | null;
  exact: boolean;
  chunks_replayed: number;
  rejected_chunks: number;
  journal_truncated: boolean;
  intervention: Record<string, unknown>;
  notes: string[];
  summary: string;
}

/**
 * Re-derive a finished episode from its record. Zero divergence means the
 * commands and refusals follow from the journal with nothing hidden.
 */
export async function replayDeployment(deploymentId: string): Promise<ReplayResult> {
  const { data } = await client.post<ReplayResult>(
    `/deployments/${deploymentId}/replay`,
  );
  return data;
}

export interface ChainVerification {
  ok: boolean;
  checked: number;
  problems: string[];
  head_hash: string;
  public_key: string;
  /** True when the caller supplied the key, so the verdict is about theirs. */
  verified_against_supplied_key?: boolean;
  key_ephemeral: boolean;
}

/**
 * Record a deliberate widening of a safety envelope.
 *
 * Widening is allowed — an integrator who knows their cell may genuinely need
 * it — but not silently. This puts a named person and a stated reason in the
 * signed chain before the fields become editable.
 */
export async function recordEnvelopeOverride(
  projectId: string,
  reason: string,
): Promise<LedgerEvent> {
  const { data } = await client.post<LedgerEvent>(
    `/projects/${projectId}/ledger/events`,
    {
      channel: "intent",
      event_type: "envelope.override_unlocked",
      action: "Operator unlocked the safety envelope fields for editing",
      rationale: reason,
    },
  );
  return data;
}

export async function listLedgerEvents(
  projectId: string,
  channel?: "intent" | "egress",
  deploymentId?: string,
): Promise<LedgerEvent[]> {
  const params: Record<string, string> = {};
  if (channel) params.channel = channel;
  if (deploymentId) params.deployment_id = deploymentId;
  const { data } = await client.get<LedgerEvent[]>(
    `/projects/${projectId}/ledger/events`,
    { params: Object.keys(params).length ? params : undefined },
  );
  return data;
}

export async function getLedgerEvent(eventId: string): Promise<LedgerEvent> {
  const { data } = await client.get<LedgerEvent>(`/ledger/events/${eventId}`);
  return data;
}

export async function verifyLedger(
  projectId: string,
): Promise<ChainVerification> {
  const { data } = await client.get<ChainVerification>(
    `/projects/${projectId}/ledger/verify`,
  );
  return data;
}

/* ------------------------------------------------------------------ */
/*  Environment Variations                                             */
/* ------------------------------------------------------------------ */

export interface VariationRequest {
  count?: number;       // default 5
  randomize?: string[]; // default ["lighting","object_positions","textures"]
}

export async function generateEnvironmentVariations(
  envId: string,
  payload?: VariationRequest,
): Promise<Environment[]> {
  const { data } = await client.post<Environment[]>(
    `/environments/${envId}/variations`,
    payload ?? {},
  );
  return data;
}

export async function getEnvironmentById(envId: string): Promise<Environment> {
  const { data } = await client.get<Environment>(`/environments/${envId}`);
  return data;
}

/* ------------------------------------------------------------------ */
/*  Contact / pilot enquiries (public — no auth required)              */
/* ------------------------------------------------------------------ */

export async function submitContact(
  payload: ContactRequest,
): Promise<ContactReceipt> {
  const { data } = await client.post<ContactReceipt>("/contact", payload);
  return data;
}
